import React from "react";
import { BookOpen } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Localizer } from "@/main/react/Types";
import type { LocalizationKey } from "@/main/util/LocalizationUtil";

/**
 * The five product guides, slug → public path.
 *
 * Mirrors `GuideRegistry.GUIDES` on the backend, which owns the routes, the security allowlist and
 * the sitemap. There is no generated contract between the two — the guides are server-rendered JTE
 * pages that this app only ever *links to*, so nothing here is fetched and nothing would fail loudly
 * if a slug drifted. `GuideLink.spec.tsx` pins the pair instead: it is a five-line frozen map, and a
 * five-line map with a test beside it is cheaper than a build-time contract for a set that changes
 * about once a year.
 *
 * Every entry is a real path on a real public route. If a guide is ever removed from the registry,
 * remove it here in the same task — a link to a dead slug renders the app's 404, exactly the failure
 * shape AGENTS.md rule 34 describes for flag-gated routes.
 */
export const GUIDE_PATHS = {
    publishing: "/guides/publishing",
    replying: "/guides/replying",
    groups: "/guides/groups",
    series: "/guides/series",
    "reaching-readers": "/guides/reaching-readers",
} as const;

export type GuideSlug = keyof typeof GUIDE_PATHS;

type GuideLinkProps = {
    /** Injected typed localizer — never `LocalizationUtil.get` at the call site (AGENTS.md rule 11). */
    localizer: Localizer;
    /** Which guide this points at. The path comes from `GUIDE_PATHS`; call sites never write one. */
    guide: GuideSlug;
    /**
     * The sentence to render. Supplied by the caller rather than derived from `guide` on purpose:
     * only the guides that something actually links to have a `guides.link.*` label, so the label
     * set stays free of keys nothing reads. Adding a new link site means adding its key in the same
     * task. See the design doc's "As built — stages 1 and 2" §1.
     */
    textKey: LocalizationKey;
    className?: string;
    /** Suffix for the stable browser-test hook, e.g. "create-group" → `guide-link-create-group`. */
    surface: string;
    /**
     * Open the guide in a new tab. Only for a call site that sits inside a half-filled form (the
     * create-series dialog, `DASHBOARD-116` / r41 V6), where a same-tab navigation would discard what
     * the member has typed. Default same-tab: everywhere else the guide is the next page to read.
     */
    openInNewTab?: boolean;
};

/**
 * The contextual pointer into a product guide: one muted line with a book glyph.
 *
 * Deliberately **not** a modal, a coach-mark, a tooltip or a dismissible tip. The onboarding overlay
 * already occupies that register, and it is the affordance measured *not* to work — completers of the
 * six-step tour return at 11.1% against 12.6% for non-completers
 * (`2026-07-25-first-session-loop-connect-before-create-design.md`). This layer is pull, not push:
 * it sits still, next to the thing you are already doing, and costs nothing to ignore.
 *
 * A plain `<a href>`, so it survives the island being unmounted and is crawlable — the same reason the
 * sidebar entry is a `type: "link"` and not a `buttonAction`.
 */
export const GuideLink: React.FC<GuideLinkProps> = ({ localizer, guide, textKey, className, surface, openInNewTab }) => {
    return (
        <a
            href={GUIDE_PATHS[guide]}
            {...(openInNewTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            data-playwright-id={`guide-link-${surface}`}
            className={cn(
                "inline-flex items-center gap-1.5 text-sm text-muted-foreground! underline underline-offset-4",
                // `EMPTY-STATES-74` (tap half) — the border box of this anchor is its `text-sm` line
                // box, ~20 CSS px, under even WCAG 2.5.8's 24x24 floor and 16 CSS px shorter than the
                // correctly-floored primary button it sits beneath in the dashboard empty panels. It
                // is not a CTA, so `getCtaClasses` never applied.
                //
                // The floor is COMPONENT-LEVEL, which the register asked for and the `!` on the colour
                // enforces anyway (no call site can override it). Gating it on the input device is
                // what makes that safe: on a fine pointer the declaration does not apply at all, so
                // all ten call sites render byte-identically to before and the register's constraint
                // — `GuideLink` must keep looking identical beside a CTA — holds by construction. On a
                // coarse pointer, the beside-a-CTA case is exactly where a 44px CTA already sets the
                // rhythm.
                //
                // `min-h-11` over `touch-hit-44` for the reason `css/03-tap-targets.css` gives itself:
                // an invisible hit area offers no affordance that it is there. It is also the safer
                // one here — a centred 44px slug on a 20px link overhangs 12px each side, into
                // whatever the inline call sites put beside it.
                "touch-manipulation [@media(pointer:coarse)]:min-h-11",
                "standard-hover rounded-sm outline-none",
                "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
                className,
            )}
        >
            <BookOpen className="size-4 shrink-0" aria-hidden="true" />
            <span>{localizer(textKey)}</span>
        </a>
    );
};

export default GuideLink;
